import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { MOVABLE_STAGES, OPEN_STAGES } from "@/lib/opportunity-calc";
import type { DBOpportunity, OpportunitySource, OpportunityStage } from "@/lib/db-types";

/**
 * Prosesser i pipen (CRM-2.1). Audit skrives av DB-triggeren
 * log_opportunity_change med den innloggede brukeren som aktør; eieren
 * (owner_id) er den impersonerte, så en superadmin som handler på vegne av
 * en rådgiver er synlig som forskjellen mellom aktør og eier.
 */

export interface OpportunityWithRelations extends DBOpportunity {
  customer: { id: string; name: string } | null;
  /** Kalde leads har ingen kunde før første tilbud (CRM-4.3). */
  lead: { id: string; name: string } | null;
  owner: { id: string; full_name: string; avatar_url: string | null } | null;
}

const SELECT_WITH_RELATIONS =
  "*, customer:customers(id, name), lead:leads!opportunities_lead_id_fkey(id, name), owner:users!opportunities_owner_id_fkey(id, full_name, avatar_url)";

export interface OpportunityFilter {
  ownerId?: string;
  stage?: OpportunityStage;
  source?: OpportunitySource;
  customerId?: string;
  /** Default false: bare Outreach, Møte og Tilbud. */
  includeClosed?: boolean;
}

type WithEnabled<T> = T & { enabled?: boolean };

/** Prosesser etter filter. Vokser ubegrenset over tid, derfor paginert. */
export function useOpportunities({ enabled = true, ...filter }: WithEnabled<OpportunityFilter> = {}) {
  return useQuery({
    queryKey: ["opportunities", filter],
    queryFn: async (): Promise<OpportunityWithRelations[]> =>
      fetchAllPaginated<OpportunityWithRelations>(
        "opportunities",
        SELECT_WITH_RELATIONS,
        (q) => {
          let query = q;
          if (filter.ownerId) query = query.eq("owner_id", filter.ownerId);
          if (filter.customerId) query = query.eq("customer_id", filter.customerId);
          if (filter.source) query = query.eq("source", filter.source);
          if (filter.stage) query = query.eq("stage", filter.stage);
          else if (!filter.includeClosed) query = query.in("stage", [...OPEN_STAGES]);
          return query;
        },
        ["stage_entered_at", "id"],
      ),
    enabled,
    staleTime: 60 * 1000,
  });
}

const CLOSED_WINDOW_DAYS = 90;

/** Vunnet og tapt de siste 90 dagene (til «Vis avsluttede»). Paginert. */
export function useClosedOpportunities(filter: Pick<OpportunityFilter, "ownerId" | "source"> & { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["opportunities", { closed: true, ownerId: filter.ownerId, source: filter.source }],
    queryFn: async (): Promise<OpportunityWithRelations[]> => {
      const since = new Date(Date.now() - CLOSED_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
      return fetchAllPaginated<OpportunityWithRelations>(
        "opportunities",
        SELECT_WITH_RELATIONS,
        (q) => {
          let query = q.or(`and(stage.eq.won,won_at.gte.${since}),and(stage.eq.lost,lost_at.gte.${since})`);
          if (filter.ownerId) query = query.eq("owner_id", filter.ownerId);
          if (filter.source) query = query.eq("source", filter.source);
          return query;
        },
        ["stage_entered_at", "id"],
      );
    },
    enabled: filter.enabled ?? true,
    staleTime: 60 * 1000,
  });
}

/** Alle prosesser på én kunde, åpne først. Bundet på kunde. */
export function useCustomerOpportunities(customerId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["opportunities", { customerId }],
    queryFn: async (): Promise<OpportunityWithRelations[]> => {
      const { data, error } = await supabase
        .from("opportunities")
        .select(SELECT_WITH_RELATIONS)
        .eq("customer_id", customerId!)
        .order("stage_entered_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as OpportunityWithRelations[];
      const rank = (o: DBOpportunity) => (OPEN_STAGES.includes(o.stage) ? 0 : 1);
      return [...rows].sort((a, b) => rank(a) - rank(b));
    },
    enabled: !!customerId && options.enabled !== false,
    staleTime: 60 * 1000,
  });
}

export function useOpportunity(id: string | undefined) {
  return useQuery({
    queryKey: ["opportunity", id],
    queryFn: async (): Promise<OpportunityWithRelations> => {
      const { data, error } = await supabase
        .from("opportunities")
        .select(SELECT_WITH_RELATIONS)
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as unknown as OpportunityWithRelations;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

const onError = (prefix: string) => (err: Error) => toast.error(`${prefix}: ${err.message}`);

function useInvalidateOpportunity() {
  const qc = useQueryClient();
  return (row: Pick<DBOpportunity, "id" | "customer_id">) =>
    invalidateFor(qc, "opportunities", { opportunityId: row.id, customerId: row.customer_id });
}

export interface CreateOpportunityInput {
  customer_id: string;
  title: string;
  theme?: string | null;
  /** Kun Outreach eller Møte; Tilbud, Vunnet og Tapt kommer fra tilbudet. */
  stage?: Extract<OpportunityStage, "outreach" | "meeting">;
}

export function useCreateOpportunity() {
  const { user } = useAuth();
  const { activeUser } = useImpersonation();
  const invalidate = useInvalidateOpportunity();
  return useMutation({
    mutationFn: async (input: CreateOpportunityInput): Promise<DBOpportunity> => {
      const title = input.title.trim();
      if (!title) throw new Error("Tittel mangler");
      if (!activeUser?.id) throw new Error("Ingen aktiv bruker");
      const { data, error } = await supabase
        .from("opportunities")
        .insert({
          customer_id: input.customer_id,
          title,
          theme: input.theme?.trim() || null,
          owner_id: activeUser.id,
          source: "manual",
          stage: input.stage ?? "outreach",
          created_by: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBOpportunity;
    },
    onSuccess: (row) => invalidate(row),
    onError: onError("Kunne ikke opprette prosess"),
  });
}

/** Flytter mellom Outreach og Møte. Alt annet går via tilbudet eller «Marker tapt». */
export function useUpdateOpportunityStage() {
  const invalidate = useInvalidateOpportunity();
  return useMutation({
    mutationFn: async ({ id, stage }: { id: string; stage: OpportunityStage }): Promise<DBOpportunity> => {
      if (!MOVABLE_STAGES.includes(stage)) {
        throw new Error("Prosessen kan bare flyttes mellom Outreach og Møte. Lag et tilbud for å flytte den til Tilbud.");
      }
      const { data, error } = await supabase
        .from("opportunities")
        .update({ stage })
        .eq("id", id)
        .in("stage", [...MOVABLE_STAGES])
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Prosessen er allerede i Tilbud, vunnet eller tapt og kan ikke flyttes her");
      return data as DBOpportunity;
    },
    onSuccess: (row) => invalidate(row),
    onError: onError("Kunne ikke flytte prosessen"),
  });
}

/** Tapt arkiveres med årsak (påkrevd, også i databasen). Prosesser i Tilbud blir tapt via tilbudet. */
export function useMarkOpportunityLost() {
  const invalidate = useInvalidateOpportunity();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<DBOpportunity> => {
      const lostReason = reason.trim();
      if (!lostReason) throw new Error("Årsak mangler");
      const { data, error } = await supabase
        .from("opportunities")
        .update({ stage: "lost", lost_reason: lostReason })
        .eq("id", id)
        .in("stage", [...MOVABLE_STAGES])
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Prosessen er allerede avsluttet");
      return data as DBOpportunity;
    },
    onSuccess: (row) => invalidate(row),
    onError: onError("Kunne ikke markere prosessen som tapt"),
  });
}

export interface UpdateOpportunityInput {
  id: string;
  title?: string;
  theme?: string | null;
  owner_id?: string;
}

export function useUpdateOpportunity() {
  const invalidate = useInvalidateOpportunity();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateOpportunityInput): Promise<DBOpportunity> => {
      const fields: Record<string, unknown> = {};
      if (patch.title !== undefined) {
        const title = patch.title.trim();
        if (!title) throw new Error("Tittel mangler");
        fields.title = title;
      }
      if (patch.theme !== undefined) fields.theme = patch.theme?.trim() || null;
      if (patch.owner_id !== undefined) fields.owner_id = patch.owner_id;
      if (Object.keys(fields).length === 0) throw new Error("Ingen endringer å lagre");
      const { data, error } = await supabase
        .from("opportunities")
        .update(fields)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBOpportunity;
    },
    onSuccess: (row) => invalidate(row),
    onError: onError("Kunne ikke lagre prosessen"),
  });
}
